import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getInsightsErrorMessage } from '../insightsErrorMessages';
import { omitKey, parseInsight } from './useManagerAssessmentInsights.utils';

import type { QuestionInsight } from '../components/InsightsPanel';
import type { AiResultNotification } from '@/api/ai-gateway/aiHub';

import { useRequestManagerAssessmentInsights } from '@/api/cycle-evaluation-services/manager-assessment';
import { useAiResultContext, useAmelioTranslations } from '@/hooks';

/** Matches the backend's pending-request window so the FE and BE agree on when a result is lost. */
const RESULT_TIMEOUT_MS = 90_000;

export interface InsightQuestion {
    id: string;
    text: string;
    /** The competency this question is assigned to (stated in the prompt for relevance). */
    competency?: string;
}

/**
 * Triggers AI insight generation for every question of a manager assessment and collects the
 * per-question results as they stream back over SignalR. The frontend owns a correlationId per
 * question and matches each pushed result to it.
 */
export function useManagerAssessmentInsights(
    cycleInstanceId: string | undefined,
    questions: InsightQuestion[],
    /** The cycle's evaluation period bounds (ISO-8601); window the engagement report. */
    fromDate: string | null | undefined,
    toDate: string | null | undefined,
) {
    const { t } = useAmelioTranslations();
    const translateInsightError = useCallback((key: string) => t(key as never), [t]);
    const { mutateAsync: requestInsights } = useRequestManagerAssessmentInsights();
    const { subscribe, getCached } = useAiResultContext();
    const [correlationByQuestion, setCorrelationByQuestion] = useState<Record<string, string>>({});
    const [insightByQuestion, setInsightByQuestion] = useState<Record<string, QuestionInsight>>({});
    const [errorByQuestion, setErrorByQuestion] = useState<Record<string, string>>({});
    const [expected, setExpected] = useState(0);
    const pendingRef = useRef(new Set<string>());
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearResultTimeout = useCallback(() => {
        if (timeoutRef.current !== null) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    // The scope (cycle + evaluation period) determines what a generation produces. When it changes, drop
    // the previous results so the panel returns to the Generate state and the user can run it for the new
    // period (the backend treats a different period as a new scope and regenerates).
    useEffect(() => {
        setInsightByQuestion({});
        setErrorByQuestion({});
        setCorrelationByQuestion({});
        setExpected(0);
        pendingRef.current.clear();
        clearResultTimeout();
        return () => {
            pendingRef.current.clear();
            clearResultTimeout();
        };
    }, [cycleInstanceId, fromDate, toDate, clearResultTimeout]);

    const questionByCorrelation = useMemo(() => {
        const inverted: Record<string, string> = {};
        for (const [questionId, correlationId] of Object.entries(correlationByQuestion)) {
            inverted[correlationId] = questionId;
        }
        return inverted;
    }, [correlationByQuestion]);

    const clearKey = useCallback((questionId: string) => {
        setInsightByQuestion((prev) => omitKey(prev, questionId));
        setErrorByQuestion((prev) => omitKey(prev, questionId));
    }, []);

    const handleNotification = useCallback(
        (notification: AiResultNotification) => {
            const questionId = questionByCorrelation[notification.correlationId ?? ''];
            if (!questionId) {
                return;
            }

            pendingRef.current.delete(questionId);
            if (pendingRef.current.size === 0) {
                clearResultTimeout();
            }

            if (notification.success) {
                setInsightByQuestion((prev) => ({
                    ...prev,
                    [questionId]: parseInsight(notification.content),
                }));
                setErrorByQuestion((prev) => omitKey(prev, questionId));
            } else {
                // Surface the failure so the user can see it and retry this question.
                setErrorByQuestion((prev) => ({
                    ...prev,
                    [questionId]: getInsightsErrorMessage(
                        notification.errorCode,
                        notification.error,
                        translateInsightError,
                    ),
                }));
            }
        },
        [questionByCorrelation, translateInsightError, clearResultTimeout],
    );

    useEffect(() => subscribe(handleNotification), [subscribe, handleNotification]);

    // Results can arrive before the correlation map is committed (the backend pushes cached hits during
    // the POST itself) — the provider caches them, so replay anything already received for this scope.
    useEffect(() => {
        for (const correlationId of Object.values(correlationByQuestion)) {
            const cached = getCached(correlationId);
            if (cached) {
                handleNotification(cached);
            }
        }
    }, [correlationByQuestion, getCached, handleNotification]);

    const armResultTimeout = useCallback(() => {
        clearResultTimeout();
        timeoutRef.current = setTimeout(() => {
            // No result within the wait window — resolve the still-pending questions as timed out
            // so the panel shows a retryable error instead of spinning on a lost callback.
            const timedOut = [...pendingRef.current];
            pendingRef.current.clear();
            if (timedOut.length === 0) {
                return;
            }
            const message = getInsightsErrorMessage(
                'AI_SERVICE_TIMEOUT',
                null,
                translateInsightError,
            );
            setErrorByQuestion((prev) => {
                const next = { ...prev };
                for (const id of timedOut) {
                    if (!(id in next)) {
                        next[id] = message;
                    }
                }
                return next;
            });
        }, RESULT_TIMEOUT_MS);
    }, [clearResultTimeout, translateInsightError]);

    const sendRequest = useCallback(
        async (
            items: {
                questionId: string;
                questionText: string;
                competency: string;
                correlationId: string;
            }[],
        ) => {
            if (!cycleInstanceId || !fromDate || !toDate) {
                return false;
            }
            for (const item of items) {
                pendingRef.current.add(item.questionId);
            }
            armResultTimeout();
            try {
                const response = await requestInsights({
                    cycleInstanceId,
                    fromDate,
                    toDate,
                    questions: items,
                });
                // The gateway rejected the dispatch up front — no SignalR callbacks will come for the
                // newly-submitted questions, so resolve them as errors now and do not show the success toast.
                if (response.status === 'failed') {
                    for (const item of items) {
                        pendingRef.current.delete(item.questionId);
                    }
                    if (pendingRef.current.size === 0) {
                        clearResultTimeout();
                    }
                    setErrorByQuestion((prev) => {
                        const next = { ...prev };
                        for (const item of items) {
                            next[item.questionId] = getInsightsErrorMessage(
                                response.errorCode,
                                response.error,
                                translateInsightError,
                            );
                        }
                        return next;
                    });
                    return false;
                }

                // Questions already in flight: subscribe to the running job's correlationId (returned by the
                // backend) instead of the one we just sent — the backend didn't dispatch a duplicate for these.
                if (response.attached?.length) {
                    setCorrelationByQuestion((prev) => {
                        const next = { ...prev };
                        for (const { questionId, correlationId } of response.attached) {
                            next[questionId] = correlationId;
                        }
                        return next;
                    });
                }
                return true;
            } catch {
                // The request itself failed (never reaches SignalR) — mark those questions as errored.
                for (const item of items) {
                    pendingRef.current.delete(item.questionId);
                }
                if (pendingRef.current.size === 0) {
                    clearResultTimeout();
                }
                setErrorByQuestion((prev) => {
                    const next = { ...prev };
                    for (const item of items) {
                        next[item.questionId] = getInsightsErrorMessage(
                            null,
                            null,
                            translateInsightError,
                        );
                    }
                    return next;
                });
                return false;
            }
        },
        [
            cycleInstanceId,
            fromDate,
            toDate,
            requestInsights,
            translateInsightError,
            armResultTimeout,
            clearResultTimeout,
        ],
    );

    const generate = useCallback(async () => {
        if (!cycleInstanceId || questions.length === 0 || !fromDate || !toDate) {
            return;
        }

        const mapping: Record<string, string> = {};
        const items = questions.map((question) => {
            const correlationId = crypto.randomUUID();
            mapping[question.id] = correlationId;
            return {
                questionId: question.id,
                questionText: question.text,
                competency: question.competency ?? '',
                correlationId,
            };
        });

        // Reset, then register the mapping — the always-on SignalR listener is already subscribed, so
        // no result can be missed once these correlationIds are known.
        pendingRef.current.clear();
        clearResultTimeout();
        setInsightByQuestion({});
        setErrorByQuestion({});
        setCorrelationByQuestion(mapping);
        setExpected(items.length);

        // No toast while generating: results come back quickly and the panel already shows its loading
        // state, so a notification only covers part of the screen (bug #15622).
        await sendRequest(items);
    }, [cycleInstanceId, questions, fromDate, toDate, sendRequest, clearResultTimeout]);

    /** Re-run generation for a single question (after it failed), with a fresh correlationId. */
    const retryQuestion = useCallback(
        async (questionId: string) => {
            const question = questions.find((q) => q.id === questionId);
            if (!question || !cycleInstanceId || !fromDate || !toDate) {
                return;
            }
            const correlationId = crypto.randomUUID();
            setCorrelationByQuestion((prev) => ({ ...prev, [questionId]: correlationId }));
            clearKey(questionId); // back to the loading state for this question
            await sendRequest([
                {
                    questionId: question.id,
                    questionText: question.text,
                    competency: question.competency ?? '',
                    correlationId,
                },
            ]);
        },
        [questions, cycleInstanceId, fromDate, toDate, clearKey, sendRequest],
    );

    // A question is resolved once it has an insight or an error; still generating while any is pending.
    const resolvedCount = useMemo(() => {
        const ids = new Set([...Object.keys(insightByQuestion), ...Object.keys(errorByQuestion)]);
        return ids.size;
    }, [insightByQuestion, errorByQuestion]);
    const isGenerating = expected > 0 && resolvedCount < expected;

    // The engagement report needs a period window; without it the request is rejected, so gate the UI.
    const canGenerate = Boolean(cycleInstanceId && questions.length > 0 && fromDate && toDate);

    return {
        insightByQuestion,
        errorByQuestion,
        isGenerating,
        canGenerate,
        generate,
        retryQuestion,
    };
}
